<template>
  <v-container fluid class="exchange-page">
    <section class="exchange-hero">
      <div class="exchange-hero__copy">
        <div class="exchange-kicker">
          <span class="exchange-kicker__mark">
            <v-icon small>fal fa-exchange-alt</v-icon>
          </span>
          {{ $t("currency") }}
        </div>
        <h1 class="exchange-hero__title">{{ $t("exchange_rate") }}</h1>
        <p class="exchange-hero__description">
          {{ $t("exchange_rate_intro") }}
        </p>
      </div>
      <div class="exchange-hero__base">
        <span class="exchange-hero__base-label">{{ $t("base_currency") }}</span>
        <strong>{{ baseCurrency.code || "LAK" }}</strong>
        <span>{{ baseCurrency.name || "Lao Kip" }}</span>
      </div>
    </section>

    <div class="exchange-summary">
      <KpiCards :items="kpiItems" />
    </div>

    <section class="exchange-rates-section">
      <div class="exchange-section-heading">
        <div>
          <h2>{{ $t("exchange_rate") }}</h2>
          <p>{{ $t("exchange_rate_intro") }}</p>
        </div>
        <span class="exchange-section-heading__count">
          {{ currencies.length }} {{ $t("currency") }}
        </span>
      </div>

      <div v-if="loading" class="exchange-rate-grid">
        <v-skeleton-loader
          v-for="index in 3"
          :key="`exchange-skeleton-${index}`"
          type="card"
          class="exchange-skeleton"
        />
      </div>

      <div v-else-if="currencies.length" class="exchange-rate-grid">
        <article
          v-for="item in currencies"
          :key="item.id"
          class="rate-card"
          :class="{ 'rate-card--inactive': item.store_status !== 'active' }"
        >
          <div class="rate-card__header">
            <div class="rate-card__identity">
              <v-avatar size="48" class="rate-card__flag">
                <v-img :src="item.flag" :alt="item.name" />
              </v-avatar>
              <div>
                <div class="rate-card__code-row">
                  <strong>{{ item.code }}</strong>
                  <span class="rate-card__symbol">{{ item.symbol }}</span>
                </div>
                <span class="rate-card__name">{{ item.name }}</span>
              </div>
            </div>
            <span
              class="rate-status"
              :class="
                item.store_status === 'active'
                  ? 'rate-status--active'
                  : 'rate-status--inactive'
              "
            >
              <span class="rate-status__dot"></span>
              {{ item.store_status === "active" ? $t("active") : $t("inactive") }}
            </span>
          </div>

          <div class="rate-card__body">
            <span class="rate-card__label">{{ $t("exchange_rate") }}</span>
            <div class="rate-card__value-row">
              <strong>{{ currentRate(item) | NumberFormat }}</strong>
              <span>{{ baseCurrency.code || "LAK" }}</span>
            </div>
            <p class="rate-card__equation">
              1 {{ item.code }} <span>=</span> {{ currentRate(item) | NumberFormat }}
              {{ baseCurrency.code || "LAK" }}
            </p>
          </div>

          <div class="rate-card__footer">
            <div class="rate-card__switch-wrap">
              <v-switch
                v-model="item.store_status"
                :color="item.store_status === 'active' ? 'success' : 'grey'"
                @change="toggleStoreCurrencyStatus(item)"
                inset
                hide-details
                :true-value="'active'"
                :false-value="'inactive'"
                class="rate-card__switch"
              />
              <span>{{ $t("active") }}</span>
            </div>
            <div class="rate-card__actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="rate-card__action"
                    v-bind="attrs"
                    v-on="on"
                    @click="getHistory(item)"
                  >
                    <v-icon small>fal fa-history</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("view_history") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="rate-card__action rate-card__action--edit"
                    v-bind="attrs"
                    v-on="on"
                    @click="editItem(item)"
                  >
                    <v-icon small>fal fa-edit</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("edit") }}</span>
              </v-tooltip>
            </div>
          </div>
        </article>
      </div>

      <div v-else class="exchange-empty">
          <v-icon size="34">fal fa-money-bill</v-icon>
        <strong>{{ $t("no_data") }}</strong>
      </div>
    </section>

    <app-custom-dialog
      v-model="dialog"
      :footer="true"
      title="modify_exchange_rate"
      @submit="submitForm"
      width="400"
    >
      <div slot="activator">
        <CurrencyInput v-model="form.exchange_rate" label="rate" />
      </div>
    </app-custom-dialog>

    <app-custom-dialog
      v-model="dialogView"
      :footer="false"
      title="view_history"
    >
      <div slot="activator">
        <v-data-table
          :headers="headers2"
          :items="histories"
          class="exchange-rate-table"
          hide-default-footer
        >
          <template #[`item.index`]='{ index }'>
            {{ index + 1 }}
          </template>
          <template #[`item.name`]> {{ form.name }} </template>
          <template #[`item.exchange_rate`]='{ item }'>
            {{ item.exchange_rate | NumberFormat }}
          </template>
          <template #[`item.status`]='{ item }'>
            <v-chip
              small
              class="rounded-lg"
              :color="item.status === 'active' ? 'success' : 'error'"
              text-color="white"
            >
              {{ $t(item.status) }}
            </v-chip>
          </template>
          <template #[`item.created_at`]='{ item }'>
            {{ item.created_at | created_at }}
          </template>
        </v-data-table>
      </div>
    </app-custom-dialog>
  </v-container>
</template>

<script>
import { success } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import CurrencyInput from "@/components/CurrencyInput";

export default {
  mixins: [myMixin],
  components: { CurrencyInput },
  data: () => ({
    dialogView: false,
    dialog: false,
    loading: true,
    currencies: [],
    histories: [],
    form: {},
  }),

  computed: {
    kpiItems() {
      return [
        {
          key: "currency",
          title: "currency",
          icon: "fal fa-money-bill",
          value: this.currencies.length,
          note: this.$t("managed_rates"),
        },
        {
          key: "active",
          title: "active",
          icon: "fal fa-check-circle",
          iconColor: "success",
          tone: "success",
          value: this.activeCurrencyCount,
          note: this.$t("active_currencies"),
        },
        {
          key: "rate",
          title: "rate_setup",
          icon: "fal fa-chart-line",
          value: this.baseCurrency.code || "LAK",
          note: this.$t("rate_setup_hint"),
        },
      ];
    },
    baseCurrency() {
      return (
        this.currencies.find((currency) => currency.code === "LAK") ||
        this.currencies[0] ||
        {}
      );
    },
    activeCurrencyCount() {
      return this.currencies.filter((currency) => currency.store_status === "active")
        .length;
    },
    headers2() {
      return [
        { text: this.$t("id"), value: "index" },
        { text: this.$t("currency"), value: "name", sortable: false },
        { text: this.$t("exchange_rate"), value: "exchange_rate" },
        { text: this.$t("status"), value: "status", sortable: false },
        {
          text: this.$t("created_at"),
          value: "created_at",
          align: "right",
          sortable: false,
        },
      ];
    },
  },

  mounted() {
    this.fetExchange();
  },

  methods: {
    currentRate(item) {
      return item.store_exchange_rate ?? item.exchange_rate ?? 1;
    },
    async fetExchange() {
      this.loading = true;
      try {
        const { data } = await this.$http.get("get_all_currencies");
        this.currencies = data.data || [];
      } catch (error) {
        console.log("error", error);
      } finally {
        this.loading = false;
      }
    },
    async toggleStoreCurrencyStatus(currency) {
      try {
        const { data } = await this.$http.post(
          `toggle_store_currency_status/${currency.id}`
        );
        if (data.status) {
          await success(
            this,
            data.message || "Store currency status updated successfully"
          );
          if (data.data && data.data.status) {
            currency.store_status = data.data.status;
          }
        }
      } catch (error) {
        console.log("error", error);
      }
    },
    editItem(item) {
      this.form = {
        id: item.id,
        exchange_rate: this.currentRate(item),
      };
      this.dialog = true;
    },
    async submitForm() {
      try {
        const body = {
          exchange_rate: this.form.exchange_rate,
          currency_id: this.form.id,
        };
        const { data } = await this.$http.post("exchange_rate", body);
        success(this, data.message);
        this.dialog = false;
        await this.fetExchange();
      } catch (error) {
        console.log("error", error);
      }
    },
    async getHistory(item) {
      try {
        const { data } = await this.$http.get(`exchange_rate/${item.id}`);
        this.form = item;
        this.histories = data.data;
        this.dialogView = true;
      } catch (error) {
        console.log("error", error);
      }
    },
  },
};
</script>

<style scoped>
.exchange-page {
  min-height: 100%;
  background: #f7f8fc;
}

.exchange-hero {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  max-width: 1320px;
  margin: 0 auto 30px;
  padding: 10px 2px 27px;
  border-bottom: 1px solid #dedee8;
  color: #252039;
}

.exchange-hero::after {
  display: none;
}

.exchange-hero__copy {
  position: relative;
  z-index: 1;
}

.exchange-kicker {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7865be;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.exchange-kicker__mark {
  display: inline-flex;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  border: 1px solid #5b39c9;
  border-radius: 10px;
  background: #eeeafd;
  color: #5b39c9;
}

.exchange-hero__title {
  margin: 12px 0 6px;
  color: #252039;
  font-size: clamp(30px, 4vw, 44px);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.05;
}

.exchange-hero__description {
  max-width: 560px;
  margin: 0;
  color: #858a9b;
  font-size: 14px;
  line-height: 1.6;
}

.exchange-hero__base {
  display: grid;
  min-width: 230px;
  grid-template-columns: auto 1fr;
  column-gap: 11px;
  align-items: center;
  padding: 11px 16px;
  border: 1px solid #e2deef;
  border-radius: 12px;
  background: #fff;
}

.exchange-hero__base-label {
  grid-column: 1 / -1;
  margin-bottom: 3px;
  color: #858a9b;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.exchange-hero__base strong {
  color: #5b39c9;
  font-size: 24px;
  letter-spacing: -0.03em;
}

.exchange-hero__base span:last-child {
  color: #858a9b;
  font-size: 12px;
}

.exchange-summary {
  max-width: 1320px;
  margin: 0 auto 34px;
}

.exchange-rates-section {
  max-width: 1320px;
  margin: 0 auto;
}

.exchange-section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.exchange-section-heading h2 {
  margin: 0;
  color: #252039;
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.025em;
}

.exchange-section-heading p {
  margin: 5px 0 0;
  color: #858a9b;
  font-size: 13px;
}

.exchange-section-heading__count {
  padding: 7px 11px;
  border-radius: 8px;
  background: #edeafb;
  color: #6451ba;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.exchange-rate-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.rate-card,
.exchange-skeleton {
  min-height: 260px;
  border: 1px solid #e8eaf2;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(47, 39, 83, 0.04);
}

.rate-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.rate-card:hover {
  border-color: #cfc5f5;
  box-shadow: 0 14px 28px rgba(47, 39, 83, 0.08);
  transform: translateY(-2px);
}

.rate-card--inactive {
  background: #fbfbfd;
}

.rate-card__header,
.rate-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.rate-card__header {
  padding: 20px 20px 16px;
}

.rate-card__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.rate-card__flag {
  border: 1px solid #ececf3;
  box-shadow: 0 4px 10px rgba(39, 31, 72, 0.08);
}

.rate-card__code-row {
  display: flex;
  align-items: center;
  gap: 7px;
}

.rate-card__code-row strong {
  color: #292440;
  font-size: 18px;
  letter-spacing: -0.02em;
}

.rate-card__symbol {
  padding: 3px 6px;
  border-radius: 5px;
  background: #f2effc;
  color: #7968bd;
  font-size: 11px;
  font-weight: 800;
}

.rate-card__name {
  display: block;
  max-width: 140px;
  overflow: hidden;
  color: #8a8e9c;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rate-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.rate-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.rate-status--active {
  color: #159465;
}

.rate-status--active .rate-status__dot {
  background: #29b77f;
  box-shadow: 0 0 0 3px #e1f7ed;
}

.rate-status--inactive {
  color: #9a9eab;
}

.rate-status--inactive .rate-status__dot {
  background: #b3b7c1;
}

.rate-card__body {
  flex: 1;
  padding: 18px 20px 20px;
  border-top: 1px solid #f0f0f5;
  border-bottom: 1px solid #f0f0f5;
}

.rate-card__label {
  color: #8c91a0;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.rate-card__value-row {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-top: 7px;
}

.rate-card__value-row strong {
  color: #5b39c9;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.05em;
}

.rate-card--inactive .rate-card__value-row strong {
  color: #858895;
}

.rate-card__value-row span {
  color: #777c8d;
  font-size: 13px;
  font-weight: 700;
}

.rate-card__equation {
  margin: 5px 0 0;
  color: #9297a6;
  font-size: 11px;
}

.rate-card__equation span {
  padding: 0 3px;
  color: #c0c3cc;
}

.rate-card__footer {
  min-height: 62px;
  padding: 10px 16px 10px 20px;
}

.rate-card__switch-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #858a99;
  font-size: 11px;
}

.rate-card__switch {
  margin: 0;
}

.rate-card__actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.rate-card__action {
  color: #7a6ac0 !important;
}

.rate-card__action--edit {
  color: #159465 !important;
}

.exchange-empty {
  display: flex;
  min-height: 240px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px dashed #d9dce7;
  border-radius: 18px;
  color: #9297a6;
}

/* =========================================
   DARK MODE OVERRIDES
========================================= */
.theme--dark.exchange-page,
.theme--dark .exchange-page {
  background: transparent !important;
}

.theme--dark .exchange-hero {
  border-bottom-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.theme--dark .exchange-kicker {
  color: #a5b4fc;
}

.theme--dark .exchange-kicker__mark {
  background: rgba(129, 140, 248, 0.15);
  border-color: rgba(129, 140, 248, 0.35);
  color: #c4b5fd;
}

.theme--dark .exchange-hero__title {
  color: #ffffff;
}

.theme--dark .exchange-hero__description {
  color: #94a3b8;
}

.theme--dark .exchange-hero__base {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .exchange-hero__base-label,
.theme--dark .exchange-hero__base span:last-child {
  color: #94a3b8;
}

.theme--dark .exchange-hero__base strong {
  color: #a5b4fc;
}

.theme--dark .exchange-section-heading h2 {
  color: #ffffff;
}

.theme--dark .exchange-section-heading p {
  color: #94a3b8;
}

.theme--dark .exchange-section-heading__count {
  background: rgba(129, 140, 248, 0.18);
  color: #c4b5fd;
}

.theme--dark .rate-card,
.theme--dark .exchange-skeleton {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

.theme--dark .rate-card:hover {
  border-color: rgba(154, 140, 240, 0.45);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
}

.theme--dark .rate-card--inactive {
  background: #181825;
}

.theme--dark .rate-card__flag {
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .rate-card__code-row strong {
  color: #ffffff;
}

.theme--dark .rate-card__symbol {
  background: rgba(129, 140, 248, 0.15);
  color: #c4b5fd;
}

.theme--dark .rate-card__name {
  color: #94a3b8;
}

.theme--dark .rate-status--active {
  color: #34d399;
}

.theme--dark .rate-status--active .rate-status__dot {
  background: #34d399;
  box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2);
}

.theme--dark .rate-status--inactive {
  color: #64748b;
}

.theme--dark .rate-card__body {
  background: #262638;
  border-top-color: rgba(255, 255, 255, 0.06);
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .rate-card__label {
  color: #94a3b8;
}

.theme--dark .rate-card__value-row strong {
  color: #a5b4fc;
}

.theme--dark .rate-card--inactive .rate-card__value-row strong {
  color: #64748b;
}

.theme--dark .rate-card__value-row span {
  color: #94a3b8;
}

.theme--dark .rate-card__equation {
  color: #94a3b8;
}

.theme--dark .rate-card__equation span {
  color: #64748b;
}

.theme--dark .rate-card__switch-wrap {
  color: #94a3b8;
}

.theme--dark .rate-card__action {
  color: #a5b4fc !important;
}

.theme--dark .rate-card__action--edit {
  color: #34d399 !important;
}

.theme--dark .exchange-empty {
  border-color: rgba(255, 255, 255, 0.12);
  background: #1e1e2d;
  color: #94a3b8;
}

@media (max-width: 1100px) {
  .exchange-rate-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .exchange-page {
    padding: 18px 12px 32px !important;
  }

  .exchange-hero {
    align-items: stretch;
    flex-direction: column;
    padding: 10px 0 22px;
  }

  .exchange-hero::after {
    display: none;
  }

  .exchange-hero__base {
    min-width: 0;
  }

  .exchange-summary {
    margin-bottom: 28px;
  }

  .exchange-section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .exchange-rate-grid {
    grid-template-columns: 1fr;
  }
}
</style>
